// Weekly tracking ranking + award ceremony helpers.
// Score combines three rewards (no penalties — keep it positive):
//   • Tracking percentage of personal weekly hours (sick/vacation reduce target)
//   • +3 points per task closed this week (anyone involved counts)
//   • +20 points per project closed this week within deadline
//   • +5 points per project closed this week after deadline (still credit)
import { durationMin, isoFromDate, startOfWeek, pad } from './format.js';
import { stripMentions } from './mentions.js';

export const DEFAULT_WEEKLY_HOURS = 40;
const WORKDAYS_PER_WEEK = 5;

export const POINTS_PER_TASK = 3;
export const POINTS_PROJECT_ON_TIME = 20;
export const POINTS_PROJECT_LATE = 5;
export const POINTS_DESCRIPTION_PER_50_CHARS = 1;
export const POINTS_DESCRIPTION_CAP = 10;

// Reference week so we can scale bonus points relative to part-time / absence
const BASELINE_MINUTES = DEFAULT_WEEKLY_HOURS * 60;
const SCALE_CAP = 2.5;  // anyone below ~16h baseline still gets ≤2.5x

// Maps a description string to the bonus points it earns the creator.
// @-Mention-Tokens werden auf reines „@Name" reduziert, damit man den Bonus
// nicht durch Erwähnungen aufbläht.
export function descriptionBonusPoints(desc) {
  if (!desc) return 0;
  const len = stripMentions(desc).trim().length;
  if (len < 50) return 0;
  return Math.min(POINTS_DESCRIPTION_CAP, Math.floor(len / 50) * POINTS_DESCRIPTION_PER_50_CHARS);
}

function expectedMinutesForUser(profile) {
  const weeklyHours = Number(profile?.weekly_hours ?? DEFAULT_WEEKLY_HOURS);
  if (!Number.isFinite(weeklyHours) || weeklyHours <= 0) return 0;
  return weeklyHours * 60;
}

function absentWeekdaysInRange(userId, weekStartDate, weekEndDate, absences) {
  if (!absences || absences.length === 0) return 0;
  const userAbs = absences.filter(a => a.user_id === userId);
  if (userAbs.length === 0) return 0;

  let count = 0;
  for (let d = new Date(weekStartDate); d.getTime() <= weekEndDate.getTime(); d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day === 0 || day === 6) continue;
    const dayMs = d.getTime();
    const covered = userAbs.some(a => {
      const s = new Date(a.start_date).getTime();
      const e = new Date(a.end_date).getTime();
      return dayMs >= s && dayMs <= e;
    });
    if (covered) count++;
  }
  return count;
}

// Returns user→{tasksDone, projectsOnTime, projectsLate, descriptionPoints, projectsCreated}
// aggregated for the week.
function computeBonusesForWeek(profiles, tasks, projects, involvedByProject, weekStartMs, weekEndMs) {
  const map = {};
  for (const p of profiles) {
    map[p.id] = {
      tasksDone: 0, projectsOnTime: 0, projectsLate: 0, descriptionPoints: 0, projectsCreated: 0,
      // The concrete items behind each count, so the UI can list them on click.
      doneTasks: [], onTimeProjects: [], lateProjects: [], createdProjects: [],
    };
  }

  // Tasks: credit to done_by if they marked it during this week
  for (const t of (tasks || [])) {
    if (!t.done || !t.done_at || !t.done_by) continue;
    const ms = new Date(t.done_at).getTime();
    if (ms < weekStartMs || ms > weekEndMs) continue;
    if (map[t.done_by]) {
      map[t.done_by].tasksDone += 1;
      map[t.done_by].doneTasks.push(t);
    }
  }

  // Projects: credit each involved member when project was closed this week +
  // award the creator a description bonus when the project was created this week
  for (const pr of (projects || [])) {
    // Description bonus on creation
    if (pr.created_by && pr.created_at) {
      const createdMs = new Date(pr.created_at).getTime();
      if (createdMs >= weekStartMs && createdMs <= weekEndMs) {
        const slot = map[pr.created_by];
        if (slot) {
          const pts = descriptionBonusPoints(pr.description);
          if (pts > 0) {
            slot.descriptionPoints += pts;
            slot.projectsCreated += 1;
            slot.createdProjects.push(pr);
          }
        }
      }
    }

    if (pr.status !== 'done' || !pr.completed_at) continue;
    const closedMs = new Date(pr.completed_at).getTime();
    if (closedMs < weekStartMs || closedMs > weekEndMs) continue;

    const onTime = pr.deadline
      ? new Date(pr.completed_at).toISOString().slice(0, 10) <= pr.deadline
      : true;

    const involved = involvedByProject?.[pr.id] || [];
    for (const uid of involved) {
      if (!map[uid]) continue;
      if (onTime) { map[uid].projectsOnTime += 1; map[uid].onTimeProjects.push(pr); }
      else        { map[uid].projectsLate   += 1; map[uid].lateProjects.push(pr); }
    }
  }

  return map;
}

export function computeWeeklyRanking(profiles, timeEntries, weekStartDate, options = {}) {
  const {
    absences = [],
    tasks = [],
    projects = [],
    involvedByProject = {},
  } = options;

  const startIso = isoFromDate(weekStartDate);
  const endDate = new Date(weekStartDate);
  endDate.setDate(endDate.getDate() + 6);
  const endIso = isoFromDate(endDate);

  const weekStartMs = weekStartDate.getTime();
  const weekEndMs   = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59).getTime();
  const bonuses = computeBonusesForWeek(profiles, tasks, projects, involvedByProject, weekStartMs, weekEndMs);

  const rows = profiles.map(p => {
    // Only entries created on the same local calendar day as entry_date count
    // toward the contest. Late ("nachgetragen") entries still exist but are
    // tallied separately so the user can see they didn't help the score.
    let totalMinutes = 0;
    let lateMinutes = 0;
    for (const e of timeEntries) {
      if (e.user_id !== p.id) continue;
      if (e.entry_date < startIso || e.entry_date > endIso) continue;
      const min = durationMin(e.from_time, e.to_time);
      const enteredOn = e.created_at ? isoFromDate(new Date(e.created_at)) : null;
      if (enteredOn && enteredOn !== e.entry_date) {
        lateMinutes += min;
      } else {
        totalMinutes += min;
      }
    }

    const fullWeekMinutes = expectedMinutesForUser(p);
    const dailyMinutes = fullWeekMinutes / WORKDAYS_PER_WEEK;
    const absentDays = absentWeekdaysInRange(p.id, weekStartDate, endDate, absences);
    const expectedMinutes = Math.max(0, dailyMinutes * (WORKDAYS_PER_WEEK - absentDays));
    const percent = expectedMinutes > 0
      ? Math.round((totalMinutes / expectedMinutes) * 100)
      : 0;

    const bonus = bonuses[p.id] || {
      tasksDone: 0, projectsOnTime: 0, projectsLate: 0, descriptionPoints: 0, projectsCreated: 0,
      doneTasks: [], onTimeProjects: [], lateProjects: [], createdProjects: [],
    };

    // Scale bonus points so part-time and partially-absent members aren't
    // penalised for completing fewer absolute items per week.
    const scaleFactor = expectedMinutes > 0
      ? Math.min(SCALE_CAP, BASELINE_MINUTES / expectedMinutes)
      : 1;

    const tasksBonus         = Math.round(bonus.tasksDone       * POINTS_PER_TASK         * scaleFactor);
    const projectsOnTimeBonus = Math.round(bonus.projectsOnTime  * POINTS_PROJECT_ON_TIME   * scaleFactor);
    const projectsLateBonus   = Math.round(bonus.projectsLate    * POINTS_PROJECT_LATE      * scaleFactor);
    const descriptionBonus    = Math.round(bonus.descriptionPoints * scaleFactor);
    const bonusPoints = tasksBonus + projectsOnTimeBonus + projectsLateBonus + descriptionBonus;
    const score = percent + bonusPoints;

    return {
      profile: p,
      totalMinutes, lateMinutes, expectedMinutes, absentDays, percent,
      tasksDone: bonus.tasksDone,
      projectsOnTime: bonus.projectsOnTime,
      projectsLate: bonus.projectsLate,
      descriptionPoints: bonus.descriptionPoints,
      projectsCreated: bonus.projectsCreated,
      doneTasks: bonus.doneTasks,
      onTimeProjects: bonus.onTimeProjects,
      lateProjects: bonus.lateProjects,
      createdProjects: bonus.createdProjects,
      tasksBonus, projectsOnTimeBonus, projectsLateBonus, descriptionBonus,
      bonusPoints,
      scaleFactor,
      score,
    };
  });

  // Sort by score desc; tie-break by raw tracked minutes
  rows.sort((a, b) => b.score - a.score || b.totalMinutes - a.totalMinutes);

  // Assign ranks (ties share rank)
  let lastScore = null;
  let lastRank = 0;
  rows.forEach((row, idx) => {
    if (row.score !== lastScore) {
      lastRank = idx + 1;
      lastScore = row.score;
    }
    row.rank = lastRank;
  });
  return rows;
}

export function lastWeekStart(d = new Date()) {
  const thisWeek = startOfWeek(d);
  thisWeek.setDate(thisWeek.getDate() - 7);
  return thisWeek;
}

export function isoWeekKey(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${pad(week)}`;
}

export const WEEKLY_GOAL_MINUTES = DEFAULT_WEEKLY_HOURS * 60;
